import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { ImageMarkerBackgroundSubsection } from 'components/minute/sidebar/MarkerSidebar/ImageMarkerSidebar/ImageMarkerBackgroundSubsection';
import { ImageMarkerIconSection } from 'components/minute/sidebar/MarkerSidebar/ImageMarkerSidebar/ImageMarkerIconSection';
import { ImageGalleriesSubsection } from 'components/minute/sidebar/MarkerSidebar/shared/ImageGalleriesSubsection';
import { SliderValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SliderValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import {
    recalculateCoordinatesForNewImage,
    rotateRectangleCoordinates
} from 'hooks/coordinates/useCoordinatesComputations';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { useIconPreview } from 'hooks/marker/useIconPreview';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { useContext } from 'react';
import { IImageV2WorldMarker } from 'react-give-me-map/dist/utils/world/worldTypes';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IImageV2WorldMarker;
}

const FILL_OPACITY_STEPS = [0.1, 0.25, 0.5, 0.75, 1];

export const ImageMarkerImageSection = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();
    const worldMarker = useWorldMarker();
    const marker = worldMarker.getSingleMarkerIfOne() as IImageV2WorldMarker;

    const iconPreview = useIconPreview({
        mapRef: ctxEdit.mapRef,
        worldMarkers: ctxEdit.worldMarkers,
        markerIds: [props.marker.id],
        markerType: 'image',
        onNewImageGenerated: ({ imageUrl, imageSize, isInitialLoad }) => {
            if (isInitialLoad) {
                return;
            }

            const map = ctxEdit.mapRef?.current;
            const newCoordinates = recalculateCoordinatesForNewImage(
                map,
                props.marker.elementData.nonRotatedCoordinates,
                imageSize
            );
            const newRotatedCoordinates = rotateRectangleCoordinates(
                map,
                newCoordinates,
                props.marker.elementData.rotation
            );
            directMarkerUpdates.updateImage(props.marker.id, {
                imageUrl,
                coordinates: newRotatedCoordinates,
                nonRotatedCoordinates: newCoordinates
            });

            ctxEdit.updateWorldMarkerData(props.marker.id, {
                imageUrl,
                coordinates: newRotatedCoordinates,
                nonRotatedCoordinates: newCoordinates
            });
        },
        initialData: props.marker.elementData
    });

    function updateOpacity(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            opacity: value
        });
    }
    const updateFillOpacityDebounced = useMapheimDebounced(updateOpacity, 100);

    return (
        <S_ImageMarkerImageSection className={props.className}>
            <SidebarSection title={Localization.IMAGE} icon={{ iconName: 'image' }} topBorder>
                <ImageGalleriesSubsection
                    marker={props.marker}
                    galleryType="image"
                    order={['image', 'icon']}
                    onSelected={(data) => {
                        if (data.type === 'symbol') {
                            const color = data.color ?? props.marker.elementData.generatedIconColor;
                            iconPreview.setIcon(data.imageUrl, color);
                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                generatedIconUrl: data.imageUrl,
                                generatedImageUrl: null,
                                generatedEmoji: null
                            });
                        }
                        if (data.type === 'image') {
                            iconPreview.setImage(data.imageUrl);
                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                generatedImageUrl: data.imageUrl,
                                generatedIconUrl: null,
                                generatedEmoji: null
                            });
                        }
                    }}
                />
                {marker?.elementData.generatedIconUrl && (
                    <ImageMarkerIconSection
                        marker={props.marker}
                        onColorChanged={(color) => {
                            iconPreview.setIconColor(color);
                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                generatedIconColor: color
                            });
                        }}
                    />
                )}
                <ImageMarkerBackgroundSubsection
                    marker={props.marker}
                    onBackgroundChanged={(color) => {
                        iconPreview.setBackgroundColor(color);
                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                            generatedBackgroundColor: color
                        });
                    }}
                />
                <SidebarSubsection
                    title={Localization.OPACITY}
                    icon={{ iconName: 'diagram-venn' }}
                    disableBottomBorder
                >
                    <SidebarContent>
                        <SliderValue
                            steps={FILL_OPACITY_STEPS}
                            value={marker.elementData.opacity}
                            onValueChange={updateFillOpacityDebounced}
                        />
                    </SidebarContent>
                </SidebarSubsection>
                <S_Canvas ref={iconPreview.canvasRef} width={600} height={600} />
            </SidebarSection>
        </S_ImageMarkerImageSection>
    );
};

const S_ImageMarkerImageSection = styled.div``;

const S_Canvas = styled.canvas`
    width: 300px;
    height: 300px;
    display: none;
`;
